import { Button, Select, Card, Row, Col, Input } from 'antd';
import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { safeArray } from '../utils/helpers';
import { ActionGetDietPlanList } from '../../../store/Actions/MasterDataAction';
import { Calendar, FileText } from 'react-feather';

const { TextArea } = Input;

export const DietPlanSection = ({
    values,
    errors,
    touched,
    allDates,
    todayDate,
    setFieldValue,
    setFieldTouched,
    handleSubmit,
    loading,
    setError,
}) => {
    const dispatch = useDispatch();
    const { dietPlan: dietPlanOptions } = useSelector(state => state?.MasterData);

    // Fetch diet plan options on component mount
    useEffect(() => {
        dispatch(ActionGetDietPlanList({ Query: '?page=1&limit=100' }));
    }, [dispatch]);

    // Transform diet plan options for dropdown
    const dietPlanDropdownOptions = useMemo(() =>
        safeArray(dietPlanOptions?.docs).map(plan => ({
            label: plan.dietName,
            value: plan.dietName,
        })), [dietPlanOptions?.docs]);

    // Initialize diet plans for all dates if not already fully set
    useEffect(() => {
        if (allDates?.length > 0) {
            const currentPlans = values?.dietPlans || [];
            const missingDates = allDates.filter(date => !currentPlans.some(p => p.date === date));

            if (missingDates.length > 0) {
                const newPlans = missingDates.map(date => ({
                    date,
                    _id: '',
                    dietName: '',
                }));
                setFieldValue('dietPlans', [...currentPlans, ...newPlans], false);
            }
        }
    }, [allDates, values?.dietPlans, setFieldValue]);

    // Handle diet plan selection for a specific date
    const handleDietPlanSelect = useCallback((date, dietName) => {
        try {
            const currentDietPlans = values?.dietPlans || [];
            const selectedOption = dietPlanOptions?.docs?.find(d => d.dietName === dietName);
            const isFirstDate = allDates?.[0] === date;

            let updatedPlans;
            if (isFirstDate && dietName) {
                // If it's the first date and a value is selected, update all dates with this plan
                // But preserve existing _ids if they match the same diet name? 
                // Actually, the requirement seems to be "set all to this"
                updatedPlans = safeArray(allDates).map(d => ({
                    date: d,
                    dietName,
                    _id: selectedOption?.id || selectedOption?._id || '',
                }));
            } else {
                // Find if this date already exists in diet plans
                const existingIndex = currentDietPlans.findIndex(plan => plan.date === date);

                if (existingIndex >= 0) {
                    // Update existing entry
                    updatedPlans = currentDietPlans.map((plan, index) =>
                        index === existingIndex
                            ? { ...plan, dietName: dietName || '', _id: selectedOption?.id || selectedOption?._id || '' }
                            : plan
                    );
                } else {
                    // Add new entry
                    updatedPlans = [...currentDietPlans, {
                        date,
                        dietName: dietName || '',
                        _id: selectedOption?.id || selectedOption?._id || '',
                    }];
                }
            }

            setFieldValue('dietPlans', updatedPlans, true);
        } catch (err) {
            console.error('Error updating diet plan:', err);
            setError?.('Failed to update diet plan');
        }
    }, [values?.dietPlans, dietPlanOptions, allDates, setFieldValue, setError]);

    // Get diet plan for a specific date
    const getDietPlanForDate = useCallback((date) => {
        if (!values?.dietPlans) return undefined;
        const plan = values.dietPlans.find(p => p.date === date);
        return plan?.dietName || undefined;
    }, [values?.dietPlans]);

    return (
        <div className="card m-3">
            <div className="d-flex align-items-center mb-3">
                <Calendar size={20} style={{ marginRight: 8, color: '#2e37a4' }} />
                <h5 className="mb-0" style={{ fontWeight: 600 }}>Diet Plans</h5>
            </div>

            <Row gutter={[16, 16]}>
                {safeArray(allDates).map((date, index) => (
                    <Col xs={24} sm={12} md={8} lg={6} key={date}>
                        <Card
                            size="small"
                            style={{
                                borderRadius: 8,
                                border: getDietPlanForDate(date) ? '1px solid #52c41a' : '1px solid #d9d9d9',
                                backgroundColor: getDietPlanForDate(date) ? '#f6ffed' : '#fff'
                            }}
                        >
                            <div style={{ marginBottom: 8 }}>
                                <span style={{
                                    fontSize: '0.85rem',
                                    fontWeight: 600,
                                    color: '#1f2937',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 6
                                }}>
                                    <Calendar size={14} style={{ color: '#64748b' }} />
                                    {date}
                                </span>
                            </div>
                            <Select
                                placeholder="Select Diet Plan"
                                value={getDietPlanForDate(date)}
                                onChange={(value) => handleDietPlanSelect(date, value)}
                                options={dietPlanDropdownOptions}
                                style={{ width: '100%' }}
                                allowClear
                                showSearch
                                filterOption={(input, option) =>
                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                }
                            />
                        </Card>
                    </Col>
                ))}
            </Row>

            {(!allDates || allDates.length === 0) && (
                <div style={{ textAlign: 'center', padding: '20px', color: '#64748b' }}>
                    No dates available for diet plan assignment.
                </div>
            )}

            {/* Diet Plan Notes Section */}
            <div style={{ marginTop: 24 }}>
                <div className="d-flex align-items-center mb-2">
                    <FileText size={18} style={{ marginRight: 8, color: '#2e37a4' }} />
                    <span style={{ fontWeight: 600, fontSize: '0.95rem' }}>Diet Plan Notes</span>
                </div>
                <TextArea
                    placeholder="Enter any special dietary instructions or notes..."
                    value={values?.dietPlanNote || ''}
                    onChange={(e) => setFieldValue('dietPlanNote', e.target.value)}
                    onBlur={() => setFieldTouched('dietPlanNote', true)}
                    rows={4}
                    style={{ borderRadius: 8 }}
                />
                {errors?.dietPlanNote && touched?.dietPlanNote && (
                    <div style={{ color: '#ff4d4f', fontSize: '0.85rem', marginTop: 4 }}>
                        {errors.dietPlanNote}
                    </div>
                )}
            </div>

            <div className="d-flex justify-content-end mb-4 mt-3">
                <Button
                    type="primary"
                    className="text-left"
                    onClick={() => {
                        try {
                            handleSubmit();
                        } catch (err) {
                            console.error('Error in save button:', err);
                            setError?.('Failed to save plan');
                        }
                    }}
                    disabled={loading}
                >
                    {loading ? 'Saving...' : 'Save Plan'}
                </Button>
            </div>
        </div>
    );
};

